import React, { useCallback, useMemo } from 'react'

import {
  AttentionOutlined,
  Checkbox,
  Divider,
  InfoOutlined,
  LightSelect,
  Modal,
  Textarea,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import FeeBreakdown from './FeeBreakdown'
import PauseOffer from './PauseOffer'
import StatusBanner from '../StatusBanner'
import useCancelModal, { CANCEL_MODAL_STEP } from './useCancelModal'

function CancelSubscriptionModal({
  isOpen,
  onClose,
  onSubmit,
  subscriptionId,
  tier = null,
  feeData = null,
  feeError = null,
  isFeeLoading = false,
  locale = 'en',
  subscriptionV16Enabled = false,
  planType = 'yearly',
  showPauseOffer = false,
  onPause = null,
  isPauseLoading = false,
}) {
  const {
    step,
    handleContinueToCancel,
    handlePauseClick,
    feeBreakdown,
    hasFee,
    showReasonSelect,
    reasonSelectPosition,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    maxCustomReasonLength,
  } = useCancelModal({
    isOpen,
    onClose,
    onSubmit,
    subscriptionId,
    tier,
    feeData,
    feeError,
    isFeeLoading,
    locale,
    subscriptionV16Enabled,
    planType,
    showPauseOffer,
    onPause,
    isPauseLoading,
  })

  const isPauseOfferStep = step === CANCEL_MODAL_STEP.PAUSE_OFFER

  const translate = useCallback(
    (key, options = {}) => I18n.t(
      `subscription_management.cancel_modal.${key}`,
      { ns: 'rails', lng: locale, ...options }
    ),
    [locale]
  )

  const reasonOptions = useMemo(() => [
    { value: 'no_longer_need', label: translate('reasons.no_longer_need') },
    { value: 'short_term_project', label: translate('reasons.short_term_project') },
    { value: 'not_enough_models', label: translate('reasons.not_enough_models') },
    { value: 'not_worth_price', label: translate('reasons.not_worth_price') },
    { value: 'not_used_enough', label: translate('reasons.not_used_enough') },
    { value: 'prefer_individual', label: translate('reasons.prefer_individual') },
    { value: 'other', label: translate('reasons.other') },
  ], [translate])

  const secondaryAction = hasFee
    ? {
      content: translate('keep_subscription_button'),
      buttonProps: { onClick: handleClose },
    }
    : undefined

  const reasonSelect = showReasonSelect && (
    <>
      <div className="cancel-subscription-modal__reason">
        <label
          className="cgt-text-medium cgt-text-medium--semi-bold"
          htmlFor="cancel-reason"
        >
          {translate('reason_label')}
        </label>
        <LightSelect
          id="cancel-reason"
          name="cancel-reason"
          options={reasonOptions}
          value={selectedReason ? selectedReason.value : ''}
          onChange={handleReasonChange}
          placeholder={translate('reason_placeholder')}
          isFullWidth
        />
      </div>
      {isOtherSelected && (
        <div className="cancel-subscription-modal__reason">
          <label
            className="cgt-text-medium cgt-text-medium--semi-bold"
            htmlFor="cancel-other-reason"
          >
            {translate('other_label')}
          </label>
          <Textarea
            id="cancel-other-reason"
            placeholder={translate('other_placeholder')}
            value={otherText}
            onChange={handleOtherTextChange}
            maxLength={maxCustomReasonLength}
            rows={3}
            isNotResizable
          />
        </div>
      )}
    </>
  )

  if (isPauseOfferStep) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        header={translate('title')}
        width="small"
        canClose
        // Triangle Modal closes its internal state before calling onClose, so a close attempt
        // during an in-flight pause must be blocked here — a no-op onClose would leave the
        // modal hidden while the parent still considers it open.
        disableClose={isPauseLoading}
        customClass="cancel-subscription-modal cancel-subscription-modal--pause-offer"
      >
        <PauseOffer
          onPause={handlePauseClick}
          onContinueToCancel={handleContinueToCancel}
          subscriptionId={subscriptionId}
          tier={tier}
          isPauseLoading={isPauseLoading}
          locale={locale}
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header={translate(hasFee ? 'fee_title_header' : 'title')}
      width={hasFee ? 'medium' : 'small'}
      canClose
      customClass={
        `cancel-subscription-modal${hasFee ? ' cancel-subscription-modal--with-fee' : ''}`
      }
      primaryAction={{
        content: translate('submit_button'),
        buttonProps: {
          type: 'primary-warning',
          onClick: handleSubmit,
          disabled: isSubmitDisabled,
        },
      }}
      secondaryAction={secondaryAction}
    >
      <div className="cancel-subscription-modal__body">
        {feeError && (
          <StatusBanner
            icon={AttentionOutlined}
            description={translate('fee_error')}
            variant="danger"
          />
        )}
        {reasonSelectPosition === 'top' && showReasonSelect && (
          <>
            {reasonSelect}
            <Divider />
          </>
        )}
        {hasFee && (
          <>
            <StatusBanner
              icon={AttentionOutlined}
              description={translate('fee_warning_banner')}
              variant="attention"
            />
            <FeeBreakdown
              remainingMonths={feeBreakdown.remainingMonths}
              monthlyRate={feeBreakdown.monthlyRate}
              totalRemaining={feeBreakdown.totalRemaining}
              cancelFee={feeBreakdown.cancelFee}
              accessUntilFormatted={accessUntilFormatted}
              locale={locale}
            />
            <div className="cancel-subscription-modal__acknowledgement">
              <Checkbox
                id="cancel-fee-acknowledge"
                isChecked={isAcknowledged}
                isControlled
                label={translate('acknowledge_checkbox')}
                onChange={setIsAcknowledged}
                size="medium"
              />
            </div>
          </>
        )}
        {!hasFee && !feeError && accessUntilFormatted && (
          <StatusBanner
            icon={InfoOutlined}
            description={translate('no_fee_description', {
              date: accessUntilFormatted,
            })}
            variant="attention"
          />
        )}
        {reasonSelectPosition === 'below-banner' && reasonSelect}
      </div>
    </Modal>
  )
}

CancelSubscriptionModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func.isRequired,
  subscriptionId: number.isRequired,
  tier: string,
  feeData: shape({
    cancel_fee: string,
    access_until: string,
    remaining_months: number,
    monthly_rate: string,
    total_remaining: string,
  }),
  feeError: shape({
    message: string,
  }),
  isFeeLoading: bool,
  locale: string,
  subscriptionV16Enabled: bool,
  planType: string,
  showPauseOffer: bool,
  onPause: func,
  isPauseLoading: bool,
}

export default CancelSubscriptionModal
